import { Box, Grid, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
  Validate,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ARRIVAL_INSTRUCTIONS_MAX_LENGTH } from "../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import CustomAutocompleteMultiFreesolo from "../../../common/components/functional/customAutocomplete/CustomAutocompleteMultiFreesolo";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import isEmpty from "../../../common/helpers/isEmpty";
import {
  compareUsersByLastName,
  getCoreUserFullName,
} from "../../../common/utils/users";
import {
  createNewCustomerUser,
  selectAllCustomerUsers,
} from "../customerUsersSlice";
import { CreateCustomerUser } from "../types/commands";
import { CustomerContact } from "../types/types";
import UserForm from "../users/UserForm";
import { isEmptyPhoneNumber } from "../utils";
import { NAME_SPACE } from "./WorkLocationsForm";

type Props = {
  customerCompanyId: string;
};

const ContactInfo = ({ customerCompanyId }: Props) => {
  const [dialogValue, setDialogValue] = useState({ open: false, value: "" });
  const { t } = useTranslation();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const dispatch = useAppDispatch();

  const { status } = useAppSelector((state) => state.customerUsers);
  const isLoading = status === "pending";

  const customerUsers = useAppSelector(selectAllCustomerUsers);

  const formattedCustomerUsers = customerUsers
    .sort(compareUsersByLastName)
    .map((customerUser) => ({
      id: customerUser.id,
      name: getCoreUserFullName(customerUser),
      tooltip: `${t("talentProfile:phoneNumberLong")}: ${!isEmptyPhoneNumber(customerUser.account?.phone) ? customerUser.account?.phone : t("common:notSet")}`,
    }));

  const defaultValues: CustomerContact = {
    firstName: "",
    lastName: "",
    phone: "",
    emailAddress: "",
    title: "",
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const onClose = () => setDialogValue({ open: false, value: "" });

  const onSave = (newContactPerson: CustomerContact) => {
    const customerUser: CreateCustomerUser = {
      ...newContactPerson,
      customerCompanyId,
    };

    dispatch(createNewCustomerUser(customerUser)).then((action) => {
      if (isFulfilled(action)) {
        const customerUserCreated = action.payload.find(
          (event) => event.name === "custUsrCreated",
        );

        if (customerUserCreated) {
          const currentContactPersons = watch("talentCustomerContactPersons");

          const formattedCustomerUser = {
            id: customerUserCreated.id,
            name: `${customerUserCreated.payload.firstName} ${customerUserCreated.payload.lastName}`,
          };

          const updatedContactPersons = [
            ...currentContactPersons,
            formattedCustomerUser,
          ];

          setValue("talentCustomerContactPersons", updatedContactPersons, {
            shouldDirty: true,
          });
          onClose();
        }
      }
    });
  };

  const validateEmailAddress: Validate<string, FieldValues> = (
    emailAddress: string,
  ) => {
    const isDirty = !!methods.formState.dirtyFields["emailAddress"];

    if (!isDirty) return true;

    // TODO: move validation to backend
    const isExistingContact = customerUsers.find(
      (customerUser) => customerUser.account.emailAddress === emailAddress,
    );
    if (isExistingContact) {
      const emailInUse = t("customers:usersNS:emailInUse");
      return emailInUse;
    }

    return true;
  };

  const saveDisabled =
    !methods.formState.isDirty ||
    !isEmpty(methods.formState.errors) ||
    !methods.formState.isValid;

  return (
    <FormSection title={t(`${NAME_SPACE}:contactInfo`)}>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">{t("common:street")}</Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="street"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <CommonTextField
              {...field}
              sx={{ maxWidth: "372px" }}
              fullWidth
              error={!!errors.street}
            />
          )}
        />
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary" textAlign="right">
          {t("common:zipCode")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Box display="flex" alignItems="center" gap="0.5rem">
          <Controller
            name="zip"
            control={control}
            rules={{
              validate: {
                onlyNumbers: (value) => /^\d+$/.test(value),
                validLength: (value) => value && value.length === 5,
              },
            }}
            render={({ field }) => (
              <CommonTextField
                {...field}
                error={!!errors.zip}
                inputProps={{ maxLength: 5 }}
                sx={{ marginRight: "0.5rem" }}
              />
            )}
          />
          <Typography variant="secondary">
            {t(`${NAME_SPACE}:postOffice`)}
          </Typography>
          <Controller
            name="postOffice"
            rules={{ required: true }}
            control={control}
            render={({ field }) => (
              <CommonTextField
                {...field}
                error={!!errors.postOffice && !isLoading}
                inputProps={{ readOnly: true }}
              />
            )}
          />
        </Box>
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary" textAlign="right">
          {t("common:city")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="city"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <CommonTextField
              {...field}
              error={!!errors.city && !isLoading}
              inputProps={{ readOnly: true }}
              sx={{ marginRight: "0.5rem" }}
            />
          )}
        />
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary" textAlign="right">
          {t(`${NAME_SPACE}:contactPerson`)}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="talentCustomerContactPersons"
          control={control}
          render={({ field: { value, onChange } }) => (
            <CustomAutocompleteMultiFreesolo
              autoHighlight
              value={value}
              options={formattedCustomerUsers}
              onChange={(_, newValue, dialog) => {
                if (!dialog.dialogOpen) {
                  onChange(newValue);
                } else {
                  setDialogValue({ open: true, value: dialog.dialogValue });
                }
              }}
            />
          )}
        />
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">
          {t(`${NAME_SPACE}:arrivalInstructionsFI`)}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="descriptionForTalent"
          control={control}
          render={({ field }) => (
            <CommonTextField
              {...field}
              fullWidth
              inputProps={{ maxLength: ARRIVAL_INSTRUCTIONS_MAX_LENGTH }}
              error={!!errors.descriptionForTalent}
              multiline
              minRows={1}
              maxRows={7}
            />
          )}
        />
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">
          {t(`${NAME_SPACE}:arrivalInstructionsEN`)}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="descriptionForTalentEng"
          control={control}
          render={({ field }) => (
            <CommonTextField
              {...field}
              fullWidth
              inputProps={{ maxLength: ARRIVAL_INSTRUCTIONS_MAX_LENGTH }}
              error={!!errors.descriptionForTalentEng}
              multiline
              minRows={1}
              maxRows={7}
            />
          )}
        />
      </Grid>
      <DialogComponent open={dialogValue.open} onClose={onClose}>
        <DialogContentWrapper
          margin
          title={t("customers:workLocationNS:addCustomerContactPerson")}
          onCancel={onClose}
          onSave={methods.handleSubmit(onSave)}
          saveDisabled={saveDisabled}
        >
          <FormProvider {...methods}>
            <UserForm
              validateEmailAddress={validateEmailAddress}
              disablePrefill
            />
          </FormProvider>
        </DialogContentWrapper>
      </DialogComponent>
    </FormSection>
  );
};

export default ContactInfo;
